import { Loader2 } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/api-client";
import { API_URL } from "@/lib/api-url";
import { authClient, useSession } from "@/lib/auth-client";
import { Wordmark } from "@/shared/wordmark";
import { type InviteInfo, invitePlan, signInPath } from "./invite-plan";

const primaryClass =
  "inline-flex w-full items-center justify-between gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50";
const quietClass =
  "inline-flex items-center rounded-xl px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors";

/**
 * Where an invitation email lands, in the web app (`/invite/:id` redirects to `/#/invite/:id`) and
 * in the extension's new tab alike. As in maquette: signed out → continue with the invited address
 * and come back here (the email's link carries a one-time `key` that signs them in with no code,
 * invite-links.ts in the API; without one, or once it's used, an email code); signed in as that address → accept and
 * open the workspace; signed in as someone else → offer to switch accounts; already in the
 * workspace → open it. Expired, used and canceled links each say so.
 */
export function WorkspaceInvite() {
  const { id = "" } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  // The email's one-time sign-in key. Dropped once refused, so the page offers a code instead.
  const [key, setKey] = useState(search.get("key"));
  const navigate = useNavigate();
  const api = useApi();
  const session = useSession();
  const user = session.data?.user;
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string>();
  const [invalid, setInvalid] = useState(false);
  const accepting = useRef(false);

  // Read the invite once the session is known, so `member` reflects who is signed in.
  useEffect(() => {
    if (session.isPending) return;
    let live = true;
    api.api.invites[":id"]
      .$get({ param: { id } })
      .then(async (res) => {
        const body = (await res.json()) as InviteInfo | { error: string };
        if (!live) return;
        if (!res.ok || "error" in body) setInvalid(true);
        else setInvite(body);
      })
      .catch(() => live && setError("Couldn't load this invite. Check your connection."));
    return () => {
      live = false;
    };
  }, [api, id, session.isPending]);

  const plan = invitePlan(invite, user?.email ?? null, session.isPending);
  const acceptOnOpen = plan.kind === "open" && plan.accept;

  // The right account (or already a member): accept, make it the active workspace, open it.
  useEffect(() => {
    if (plan.kind !== "open" || !invite || accepting.current || error) return;
    accepting.current = true;
    void (async () => {
      if (acceptOnOpen) {
        const res = await api.api.invites[":id"].accept.$post({ param: { id } });
        const body = (await res.json()) as { workspaceId?: string; error?: string };
        if (!res.ok || !body.workspaceId) {
          accepting.current = false;
          return setError(body.error ?? "Couldn't accept this invite.");
        }
      }
      await authClient.organization.setActive({ organizationId: invite.workspaceId });
      navigate("/", { replace: true, state: { joined: invite.workspace } });
    })().catch(() => {
      accepting.current = false;
      setError("Couldn't accept this invite. Check your connection.");
    });
  }, [plan.kind, acceptOnOpen, invite, api, id, navigate, error]);

  // `signedOut`: the sign-in page must not bounce back here on the session it still remembers.
  const continueAs = (signedOut = false) =>
    navigate(signInPath(id, invite?.email ?? ""), { state: { signedOut } });

  // The link proved the inbox: sign in as the invited address (new account if needed) and join,
  // in one request. A used or replaced key falls back to signing in with a code.
  const signInWithLink = async () => {
    if (!invite || !key) return continueAs(true);
    setError(undefined);
    const res = await fetch(`${API_URL}/api/invites/${encodeURIComponent(id)}/sign-in`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ key }),
    }).catch(() => null);
    const body =
      res &&
      ((await res.json().catch(() => ({}))) as {
        workspaceId?: string;
        code?: string;
        error?: string;
      });
    if (!res || !body) return setError("Couldn't accept this invite. Check your connection.");
    if (res.status === 401) {
      setKey(null);
      return continueAs(true);
    }
    if (!res.ok || !body.workspaceId) return setError(body.error ?? "Couldn't accept this invite.");
    authClient.$store.notify("$sessionSignal"); // pick up the new session everywhere
    await authClient.organization.setActive({ organizationId: invite.workspaceId }).catch(() => {});
    navigate("/", { replace: true, state: { joined: invite.workspace } });
  };
  // A used invite has nothing left to sign in to: a member opens it after signing in with a code.
  const linkWorks = Boolean(key) && invite?.status === "pending";
  const onContinue = linkWorks ? signInWithLink : continueAs;

  // Signed in as another member of the workspace (often the one who sent the invite).
  const openAsMe =
    user && invite?.member
      ? async () => {
          await authClient.organization.setActive({ organizationId: invite.workspaceId });
          navigate("/", { replace: true });
        }
      : undefined;

  let body: ReactNode;
  if (invalid) {
    body = (
      <Message title="This link isn't valid.">
        It may have been replaced by a newer invite or canceled. Ask whoever invited you to send it
        again.
        <GoHome />
      </Message>
    );
  } else if (!invite || plan.kind === "loading" || (plan.kind === "open" && !error)) {
    body = (
      <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {error ?? (plan.kind === "open" ? `Opening ${invite?.workspace}…` : "Loading invitation…")}
      </div>
    );
  } else if (plan.kind === "expired") {
    body = (
      <Message title="This invite has expired.">
        Invites last 48 hours. Ask {invite.inviter ?? "the workspace owner"} to send a new one.
        <GoHome />
      </Message>
    );
  } else if (plan.kind === "used") {
    body = (
      <Message title="This invite has already been used.">
        It was for <strong className="text-foreground">{invite.email}</strong>, and you're signed in
        as <strong className="text-foreground">{user?.email}</strong>.
        <Actions>
          <SwitchAccount
            email={invite.email}
            current={user?.email}
            onContinue={continueAs}
            onStay={openAsMe}
            workspace={invite.workspace}
          />
        </Actions>
      </Message>
    );
  } else {
    body = (
      <>
        <Heading>
          {invite.inviter ?? "Someone"} invited you to join {invite.workspace}
          {invite.role === "admin" ? " as an admin" : ""}.
        </Heading>
        {plan.kind === "wrong-account" ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            This invite is for <strong className="text-foreground">{invite.email}</strong>, but
            you're signed in as <strong className="text-foreground">{user?.email}</strong>.
          </p>
        ) : linkWorks ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            You'll join as <strong className="text-foreground">{invite.email}</strong>. No password,
            and an account is created if you don't have one.
          </p>
        ) : (
          <p className="text-sm leading-relaxed text-muted-foreground">
            Continue with <strong className="text-foreground">{invite.email}</strong>. We'll email
            you a six-digit code. No password, and an account is created if you don't have one.
          </p>
        )}
        <Actions>
          <SwitchAccount
            email={invite.email}
            current={user?.email}
            onContinue={onContinue}
            onStay={openAsMe}
            workspace={invite.workspace}
          />
        </Actions>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <Wordmark />
        </div>
        <div className="space-y-4 text-center">{body}</div>
      </div>
    </div>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h1 className="text-3xl font-normal tracking-[-0.03em] text-balance">{children}</h1>;
}

function Message({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <Heading>{title}</Heading>
      <div className="text-sm leading-relaxed text-muted-foreground">{children}</div>
    </>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="flex flex-col items-center gap-2 pt-4">{children}</div>;
}

function GoHome() {
  return (
    <Actions>
      <Link to="/" className={quietClass}>
        Go to Kourai
      </Link>
    </Actions>
  );
}

/**
 * Signed out: continue as the invited address. Signed in as someone else: sign out first, then
 * continue as the invited address (maquette's flow), or stay and leave the invite alone.
 */
function SwitchAccount({
  email,
  current,
  workspace,
  onContinue,
  onStay,
}: {
  email: string;
  current?: string;
  workspace: string;
  onContinue: (signedOut?: boolean) => void | Promise<void>;
  /** Set when the current account is in the workspace too: open it as them. */
  onStay?: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  if (!current)
    return (
      <button
        type="button"
        className={primaryClass}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          await onContinue();
          setBusy(false);
        }}
      >
        <span className="truncate">Continue as {email}</span>
        <span aria-hidden="true">→</span>
      </button>
    );
  return (
    <>
      <button
        type="button"
        className={primaryClass}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          await authClient.signOut().catch(() => {});
          await onContinue(true);
          setBusy(false);
        }}
      >
        <span className="truncate">Sign out and continue as {email}</span>
        <span aria-hidden="true">→</span>
      </button>
      {onStay ? (
        <button
          type="button"
          className={quietClass}
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void onStay().catch(() => setBusy(false));
          }}
        >
          Stay as {current} and open {workspace}
        </button>
      ) : (
        <Link to="/" className={quietClass}>
          Not now
        </Link>
      )}
    </>
  );
}
